<template>
  <Card
    :footerDivider="false"
    :title="$t('publicPasswordResetNewPassword.general.title')"
  >
    <template v-slot:content>
      <p v-if="hasGeneralError" class="error">
        {{ generalErrorMessage }}
      </p>
      <div>
        <div id="password">
          <BasicTextfield
            @input="updateResetPassword"
            id="password-reset.new-password-input"
            type="password"
            :label="$t('publicPasswordResetNewPassword.password.label')"
            :password-visibility-trigger="true"
            :required="true"
            :status="passwordTextfieldStatus"
            :value="password"
            @blur="setPasswordToDefaultStatus"
            @cleared="setPasswordToDefaultStatus"
            @error="togglePasswordError"
            @keyup.enter="onSubmit"
          >
            <template v-slot:error>{{ passwordErrorMessage }}</template>
            <template v-slot:info>
              <div
                v-dompurify-html="$t('publicPasswordResetNewPassword.password.info')"
              ></div>

              <ValidationRule :valid="uppercaseValidator()">
                {{ $t('publicPasswordResetNewPassword.password.validation.uppercase') }}
              </ValidationRule>

              <ValidationRule :valid="lowercaseValidator()">
                {{ $t('publicPasswordResetNewPassword.password.validation.lowercase') }}
              </ValidationRule>

              <ValidationRule :valid="digitsValidator()">
                {{ $t('publicPasswordResetNewPassword.password.validation.digits') }}
              </ValidationRule>

              <ValidationRule :valid="specialCharacterValidator()">
                {{
                  $t(
                    'publicPasswordResetNewPassword.password.validation.specialCharacters'
                  )
                }}
              </ValidationRule>

              <ValidationRule :valid="lengthValidator()">
                {{ $t('publicPasswordResetNewPassword.password.validation.length') }}
              </ValidationRule>
            </template>
          </BasicTextfield>
        </div>
        <div class="card-row__col1"></div>
      </div>
    </template>
    <template v-slot:footer>
      <div class="footer-cta footer-cta--left">
        <CustomButton
          @click="onSubmit"
          :button-color="ButtonColor.PRIMARY"
          :pending-state="pending"
          buttonId="password-reset.jetzt-vergeben-button"
          >{{ $t('publicPasswordResetNewPassword.submit.text') }}
        </CustomButton>
        <CustomButton
          @click="redirectToLoginOrSettingsPage"
          :buttonColor="ButtonColor.LINK"
          buttonId="password-reset.new-password-abbrechen-button"
        >
          {{ $t('publicPasswordResetNewPassword.abortion.text') }}
        </CustomButton>
      </div>
    </template>
  </Card>
</template>

<script lang="ts" setup>
import {
  BasicTextfield,
  ButtonColor,
  Card,
  CustomButton,
  InputStatus,
  ValidationRule
} from 'de-zurich-ui-library'
import { navigate } from '@/utils/NavigationUtils'
import { isAuthenticated } from '@/utils/KeycloakUtils'
import { onMounted, ref } from 'vue'

import { useI18n } from 'vue-i18n'
import { is5xxError } from '@/components/account-management/accountcreation/accountcreationUtils'
import { setNewPassword } from '@/stores/accountManagementService'
import { ResponseError } from 'account-api'

const { t } = useI18n()
const props = defineProps({
  accountId: {
    type: String,
    required: true
  },
  resetCode: {
    type: String,
    required: true
  }
})

const emit = defineEmits<{
  onPasswordReset: [none: void]
}>()

const upperCaseRegex = /[A-Z]/
const lowerCaseRegex = /[a-z]/
const digitsRegex = /\d/

const password = ref('')
const passwordTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const passwordErrorMessage = ref('')
const passwordRegex = /^[a-zA-Z0-9_\-.+@&;!$%]{8,60}$/
const hasGeneralError = ref(false)
const generalErrorMessage = ref('')
const pending = ref(false)
const loggedIn = ref(false)

onMounted(async () => {
  loggedIn.value = isAuthenticated()
})

const uppercaseValidator = (): boolean => {
  return upperCaseRegex.test(password.value)
}

function lowercaseValidator(): boolean {
  return lowerCaseRegex.test(password.value)
}

function digitsValidator(): boolean {
  return digitsRegex.test(password.value)
}

function specialCharacterValidator(): boolean {
  return /[_\-.+@&;!$%]+/.test(password.value)
}

function lengthValidator(): boolean {
  return /^.{8,60}$/.test(password.value)
}

const atLeastThreeRulesValidator = (_password: string) => {
  const testResult = {
    lengthSufficient: lengthValidator(),
    uppercasePresent: uppercaseValidator(),
    lowercasePresent: lowercaseValidator(),
    digitsPresent: digitsValidator(),
    specialCharactersPresent: specialCharacterValidator(),
    enoughRulesMet: () => {
      let rulesMet = 0
      rulesMet += testResult.uppercasePresent ? 1 : 0
      rulesMet += testResult.lowercasePresent ? 1 : 0
      rulesMet += testResult.digitsPresent ? 1 : 0
      rulesMet += testResult.specialCharactersPresent ? 1 : 0
      return rulesMet >= 3
    }
  }
  return testResult.lengthSufficient && testResult.enoughRulesMet()
}

const setPasswordError = (errorMessageKey: string): void => {
  togglePasswordError()
  passwordErrorMessage.value = t(errorMessageKey)
}

const validatePassword = (): void => {
  if (!password.value) {
    setPasswordError('publicPasswordResetNewPassword.password.errors.empty')
  } else if (!lengthValidator()) {
    setPasswordError('publicPasswordResetNewPassword.password.errors.lengthViolate')
  } else if (!atLeastThreeRulesValidator(password.value)) {
    setPasswordError('publicPasswordResetNewPassword.password.errors.rulesViolated')
  } else if (!passwordRegex.test(password.value)) {
    setPasswordError(
      'publicPasswordResetNewPassword.password.errors.unsupportedCharacters'
    )
  }
}

function passwordIsInvalid(): boolean {
  validatePassword()
  return passwordTextfieldStatus.value === InputStatus.ERROR
}

const updateResetPassword = (val: string): void => {
  setPasswordToDefaultStatus()
  password.value = val
  validatePassword()
}

const togglePasswordError = (): void => {
  passwordTextfieldStatus.value = InputStatus.ERROR
}

const setPasswordToDefaultStatus = (): void => {
  passwordTextfieldStatus.value = InputStatus.DEFAULT
}

const onSubmit = async (): Promise<void> => {
  if (passwordIsInvalid()) {
    return
  }

  pending.value = true

  try {
    await setNewPassword(props.resetCode, props.accountId, password.value)
    // Success: no error thrown
    emit('onPasswordReset')
  } catch (e) {
    if (e instanceof ResponseError) {
      const status = e.response.status
      if (is5xxError(status)) {
        navigate(globalThis.ENV_CONFIG?.REDIRECTS.TECHNICAL_ERROR_PAGE)
      } else {
        // 4xx errors: display general error message
        generalErrorMessage.value = t(
          'publicPasswordResetNewPassword.general.errors.default'
        )
        hasGeneralError.value = true
      }
    } else {
      // Network or other errors
      navigate(globalThis.ENV_CONFIG?.REDIRECTS.TECHNICAL_ERROR_PAGE)
    }
  } finally {
    pending.value = false
  }
}

const redirectToLoginOrSettingsPage = (): void => {
  if (loggedIn.value) {
    navigate(globalThis.ENV_CONFIG?.REDIRECTS.PERSONAL_PROFIL_PAGE)
  } else {
    navigate(globalThis.ENV_CONFIG?.REDIRECTS.LOGIN_PAGE, true)
  }
}
</script>

<style lang="scss" scoped>
span.error-message {
  font-size: 24px;
  color: var(--input-color-error);
}

b.error-message {
  padding-left: 5px;
  color: var(--input-color-error);
}

.card {
  min-height: 250px;
}
</style>
